/**
 * Operations Summary Component
 *
 * Displays operational metrics and visualizations
 */

import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, XAxis, YAxis } from "recharts";
import type { OperationsSummary as OperationsSummaryType } from "../types/operations-types";
import { BookingLeadTimeChart } from "./booking-lead-time-chart";
import { LockedChartCard } from "./locked-chart-card";
import { MetricCard } from "./metric-card";
import { PeakHoursHeatmap } from "./peak-hours-heatmap";
import { ServiceDurationChart } from "./service-duration-chart";

interface OperationsSummaryProps {
  data: OperationsSummaryType | null;
}

export function OperationsSummary({ data }: OperationsSummaryProps) {
  const { t } = useTranslation("reports");

  // Generate mock data for locked state
  const mockMetrics = {
    cancellationRate: {
      type: "absolute" as const,
      label: t("metrics.cancellationRate.label"),
      value: {
        current: 12.5,
        previous: 15.2,
        change: -17.8,
        changePercent: -17.8,
        isIncrease: false,
        isPositive: true,
        trend: [],
      },
      format: "percentage" as const,
      inverse: true,
    },
    noShowRate: {
      type: "absolute" as const,
      label: t("metrics.noShowRate.label"),
      value: {
        current: 8.3,
        previous: 10.1,
        change: -17.8,
        changePercent: -17.8,
        isIncrease: false,
        isPositive: true,
        trend: [],
      },
      format: "percentage" as const,
      inverse: true,
    },
    averageServiceDuration: {
      type: "absolute" as const,
      label: t("metrics.avgServiceDuration.label"),
      value: {
        current: 45,
        previous: 42,
        change: 7.1,
        changePercent: 7.1,
        isIncrease: true,
        isPositive: true,
        trend: [],
      },
      format: "number" as const,
    },
    averageBookingLeadTime: {
      type: "absolute" as const,
      label: t("metrics.avgBookingLeadTime.label"),
      value: {
        current: 3.2,
        previous: 2.8,
        change: 14.3,
        changePercent: 14.3,
        isIncrease: true,
        isPositive: true,
        trend: [],
      },
      format: "number" as const,
    },
  };

  const mockLeadTimeData = [
    {
      category: "Same Day",
      currentCount: 45,
      previousCount: 38,
      currentPercentage: 25,
      previousPercentage: 22,
    },
    {
      category: "1-3 Days",
      currentCount: 82,
      previousCount: 75,
      currentPercentage: 45,
      previousPercentage: 43,
    },
    {
      category: "4-7 Days",
      currentCount: 35,
      previousCount: 40,
      currentPercentage: 19,
      previousPercentage: 23,
    },
    {
      category: "1-2 Weeks",
      currentCount: 15,
      previousCount: 18,
      currentPercentage: 8,
      previousPercentage: 10,
    },
    {
      category: "2+ Weeks",
      currentCount: 5,
      previousCount: 4,
      currentPercentage: 3,
      previousPercentage: 2,
    },
  ];

  const mockDurationData = [
    {
      category: "0-30 min",
      currentCount: 25,
      previousCount: 22,
      currentPercentage: 15,
      previousPercentage: 13,
      currentAvgDuration: 20,
      previousAvgDuration: 18,
    },
    {
      category: "30-60 min",
      currentCount: 95,
      previousCount: 88,
      currentPercentage: 55,
      previousPercentage: 52,
      currentAvgDuration: 45,
      previousAvgDuration: 44,
    },
    {
      category: "60-90 min",
      currentCount: 38,
      previousCount: 45,
      currentPercentage: 22,
      previousPercentage: 27,
      currentAvgDuration: 75,
      previousAvgDuration: 73,
    },
    {
      category: "90+ min",
      currentCount: 12,
      previousCount: 15,
      currentPercentage: 8,
      previousPercentage: 8,
      currentAvgDuration: 105,
      previousAvgDuration: 102,
    },
  ];

  // If data is null (basic tier), show locked versions
  if (!data) {
    // Mock peak hours data
    const mockPeakHoursData = [
      {
        dayOfWeek: "Monday",
        timeSlot: "09:00-10:00",
        appointmentCount: 5,
        utilizationPercentage: 40,
      },
      {
        dayOfWeek: "Monday",
        timeSlot: "10:00-11:00",
        appointmentCount: 8,
        utilizationPercentage: 65,
      },
      {
        dayOfWeek: "Monday",
        timeSlot: "11:00-12:00",
        appointmentCount: 10,
        utilizationPercentage: 80,
      },
      {
        dayOfWeek: "Monday",
        timeSlot: "14:00-15:00",
        appointmentCount: 7,
        utilizationPercentage: 55,
      },
      {
        dayOfWeek: "Tuesday",
        timeSlot: "09:00-10:00",
        appointmentCount: 6,
        utilizationPercentage: 50,
      },
      {
        dayOfWeek: "Tuesday",
        timeSlot: "10:00-11:00",
        appointmentCount: 9,
        utilizationPercentage: 70,
      },
      {
        dayOfWeek: "Tuesday",
        timeSlot: "11:00-12:00",
        appointmentCount: 12,
        utilizationPercentage: 95,
      },
      {
        dayOfWeek: "Wednesday",
        timeSlot: "10:00-11:00",
        appointmentCount: 11,
        utilizationPercentage: 85,
      },
      {
        dayOfWeek: "Wednesday",
        timeSlot: "11:00-12:00",
        appointmentCount: 13,
        utilizationPercentage: 100,
      },
      {
        dayOfWeek: "Thursday",
        timeSlot: "09:00-10:00",
        appointmentCount: 4,
        utilizationPercentage: 35,
      },
      {
        dayOfWeek: "Friday",
        timeSlot: "10:00-11:00",
        appointmentCount: 10,
        utilizationPercentage: 80,
      },
      {
        dayOfWeek: "Saturday",
        timeSlot: "09:00-10:00",
        appointmentCount: 12,
        utilizationPercentage: 95,
      },
      {
        dayOfWeek: "Saturday",
        timeSlot: "10:00-11:00",
        appointmentCount: 14,
        utilizationPercentage: 100,
      },
    ];

    const mockPeakHoursChart = <PeakHoursHeatmap data={mockPeakHoursData} />;

    const mockLeadTimeChart = (
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={mockLeadTimeData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="category" className="text-xs" tick={{ fontSize: 10 }} />
          <YAxis className="text-xs" tick={{ fontSize: 10 }} />
          <Legend wrapperStyle={{ fontSize: "12px" }} iconType="rect" />
          <Bar dataKey="currentCount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          <Bar dataKey="previousCount" fill="#94a3b8" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );

    const mockDurationChart = (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={mockDurationData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="category" className="text-xs" tick={{ fontSize: 10 }} />
          <YAxis className="text-xs" tick={{ fontSize: 10 }} />
          <Legend wrapperStyle={{ fontSize: "12px" }} iconType="rect" />
          <Bar dataKey="currentCount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          <Bar dataKey="previousCount" fill="#94a3b8" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );

    return (
      <div className="space-y-4">
        {/* Primary Metric Cards - Locked */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            metric={mockMetrics.cancellationRate}
            tooltip={t("metrics.cancellationRate.tooltip")}
            locked
          />
          <MetricCard
            metric={mockMetrics.noShowRate}
            tooltip={t("metrics.noShowRate.tooltip")}
            locked
          />
          <MetricCard
            metric={mockMetrics.averageServiceDuration}
            tooltip={t("metrics.avgServiceDuration.tooltip")}
            locked
          />
          <MetricCard
            metric={mockMetrics.averageBookingLeadTime}
            tooltip={t("metrics.avgBookingLeadTime.tooltip")}
            locked
          />
        </div>

        {/* Visualizations - Locked */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LockedChartCard
            title={t("charts.peakHours.title")}
            tooltip={t("charts.peakHours.tooltip")}
            mockContent={mockPeakHoursChart}
            height="h-auto"
          />
          <LockedChartCard
            title={t("charts.bookingLeadTime.title")}
            tooltip={t("charts.bookingLeadTime.tooltip")}
            mockContent={mockLeadTimeChart}
            height="h-auto"
          />
        </div>

        {/* Service Duration Distribution - Locked */}
        <div className="grid grid-cols-1 gap-4">
          <LockedChartCard
            title={t("charts.serviceDuration.title")}
            tooltip={t("charts.serviceDuration.tooltip")}
            mockContent={mockDurationChart}
            height="h-auto"
          />
        </div>
      </div>
    );
  }

  // Real data rendering
  return (
    <div className="space-y-4">
      {/* Primary Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          metric={{ ...data.cancellationRate, label: t("metrics.cancellationRate.label") }}
          tooltip={t("metrics.cancellationRate.tooltip")}
        />
        <MetricCard
          metric={{ ...data.noShowRate, label: t("metrics.noShowRate.label") }}
          tooltip={t("metrics.noShowRate.tooltip")}
        />
        <MetricCard
          metric={{ ...data.averageServiceDuration, label: t("metrics.avgServiceDuration.label") }}
          tooltip={t("metrics.avgServiceDuration.tooltip")}
        />
        <MetricCard
          metric={{ ...data.averageBookingLeadTime, label: t("metrics.avgBookingLeadTime.label") }}
          tooltip={t("metrics.avgBookingLeadTime.tooltip")}
        />
      </div>

      {/* Visualizations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PeakHoursHeatmap data={data.peakHours} />
        <BookingLeadTimeChart
          current={data.bookingLeadTimeDistribution.current}
          previous={data.bookingLeadTimeDistribution.previous}
        />
      </div>

      {/* Service Duration Distribution */}
      <div className="grid grid-cols-1 gap-4">
        <ServiceDurationChart
          current={data.serviceDurationDistribution.current}
          previous={data.serviceDurationDistribution.previous}
        />
      </div>
    </div>
  );
}
